import { X, Share2, Plus, Calendar, User, Pencil } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import type { ForeignWord, User as UserType } from '../../../types/word-discovery'
import { VoteButtons } from '../alternatives-voting/VoteButtons'
import type { VoteType } from '../../../types/alternatives-voting'
import { ClickableUser } from '../../ClickableUser'

interface WordDetailModalProps {
  word: ForeignWord
  users: UserType[]
  currentUser?: UserType
  userVotes: Record<string, VoteType>
  onClose: () => void
  onVote: (alternativeId: string, voteType: VoteType) => void
  onProposeAlternative: () => void
  onShare: () => void
}

export function WordDetailModal({
  word,
  users,
  currentUser,
  userVotes,
  onClose,
  onVote,
  onProposeAlternative,
  onShare,
}: WordDetailModalProps) {
  const getUserById = (userId: string) => {
    return users.find((u) => u.id === userId)
  }

  const submitter = getUserById(word.submittedBy)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <ModalBackdrop onClose={onClose} />
      <ModalContainer>
        <ModalHeader
          word={word}
          submitter={submitter}
          currentUser={currentUser}
          onShare={onShare}
          onClose={onClose}
        />
        <ModalContent
          word={word}
          userVotes={userVotes}
          getUserById={getUserById}
          onVote={onVote}
          onProposeAlternative={onProposeAlternative}
        />
      </ModalContainer>
    </div>
  )
}

interface ModalBackdropProps {
  onClose: () => void
}

function ModalBackdrop({ onClose }: ModalBackdropProps) {
  return (
    <div
      className="absolute inset-0 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    />
  )
}

function ModalContainer({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative w-full max-w-3xl max-h-[90vh] overflow-auto bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl">
      {children}
    </div>
  )
}

interface ModalHeaderProps {
  word: ForeignWord
  submitter?: UserType
  currentUser?: UserType
  onShare: () => void
  onClose: () => void
}

function ModalHeader({ word, submitter, currentUser, onShare, onClose }: ModalHeaderProps) {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  return (
    <div className="sticky top-0 z-10 bg-slate-900/95 backdrop-blur-sm border-b border-slate-800 px-6 py-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="text-sm text-slate-500 mb-1">{word.word}</div>
          <h2 className="text-3xl font-bold text-amber-400 tracking-wide">
            {word.wordArabic}
          </h2>
        </div>

        <ModalActions
          wordId={word.id}
          currentUserId={currentUser?.id}
          submittedBy={word.submittedBy}
          onShare={onShare}
          onClose={onClose}
        />
      </div>

      {submitter && (
        <SubmitterInfo submitter={submitter} submittedAt={word.submittedAt} formatDate={formatDate} />
      )}
    </div>
  )
}

interface ModalActionsProps {
  wordId: string
  currentUserId?: string
  submittedBy: string
  onShare: () => void
  onClose: () => void
}

function ModalActions({ wordId, currentUserId, submittedBy, onShare, onClose }: ModalActionsProps) {
  const navigate = useNavigate()

  return (
    <div className="flex items-center gap-2">
      {currentUserId === submittedBy && (
        <button
          onClick={() => navigate(`/word/${wordId}/edit`)}
          className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
          title="تعديل"
        >
          <Pencil className="w-5 h-5 text-slate-400 hover:text-amber-500" />
        </button>
      )}
      <button
        onClick={onShare}
        className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
        title="شارك"
      >
        <Share2 className="w-5 h-5 text-slate-400 hover:text-amber-500" />
      </button>
      <button
        onClick={onClose}
        className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
        title="إغلاق"
      >
        <X className="w-5 h-5 text-slate-400 hover:text-rose-500" />
      </button>
    </div>
  )
}

interface SubmitterInfoProps {
  submitter: UserType
  submittedAt: string
  formatDate: (dateString: string) => string
}

function SubmitterInfo({ submitter, submittedAt, formatDate }: SubmitterInfoProps) {
  return (
    <div className="flex items-center gap-2 mt-3 text-sm text-slate-400">
      <User className="w-4 h-4" />
      <span>
        قدمها <ClickableUser userId={submitter.id} userName={submitter.name} className="text-slate-400" />
      </span>
      <span className="text-slate-600">•</span>
      <Calendar className="w-4 h-4" />
      <span>{formatDate(submittedAt)}</span>
    </div>
  )
}

interface ModalContentProps {
  word: ForeignWord
  userVotes: Record<string, VoteType>
  getUserById: (userId: string) => UserType | undefined
  onVote: (alternativeId: string, voteType: VoteType) => void
  onProposeAlternative: () => void
}

function ModalContent({
  word,
  userVotes,
  getUserById,
  onVote,
  onProposeAlternative
}: ModalContentProps) {
  return (
    <div className="px-6 py-6 space-y-6">
      <WordContext context={word.context} />
      <Divider />
      <AlternativesSection
        word={word}
        userVotes={userVotes}
        getUserById={getUserById}
        onVote={onVote}
        onProposeAlternative={onProposeAlternative}
      />
    </div>
  )
}

interface WordContextProps {
  context: string
}

function WordContext({ context }: WordContextProps) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-slate-400 mb-2">
        السياق والاستخدام
      </h3>
      <p className="text-slate-300 leading-relaxed">{context}</p>
    </div>
  )
}

function Divider() {
  return <div className="h-px bg-gradient-to-r from-transparent via-slate-700 to-transparent" />
}

interface AlternativesSectionProps {
  word: ForeignWord
  userVotes: Record<string, VoteType>
  getUserById: (userId: string) => UserType | undefined
  onVote: (alternativeId: string, voteType: VoteType) => void
  onProposeAlternative: () => void
}

function AlternativesSection({
  word,
  userVotes,
  getUserById,
  onVote,
  onProposeAlternative
}: AlternativesSectionProps) {
  return (
    <>
      <AlternativesHeader
        alternativesCount={word.alternativesCount}
        onProposeAlternative={onProposeAlternative}
      />
      <AlternativesList
        alternatives={word.alternatives}
        topAlternativeId={word.topAlternative?.id}
        userVotes={userVotes}
        getUserById={getUserById}
        onVote={onVote}
      />
    </>
  )
}

interface AlternativesHeaderProps {
  alternativesCount: number
  onProposeAlternative: () => void
}

function AlternativesHeader({ alternativesCount, onProposeAlternative }: AlternativesHeaderProps) {
  return (
    <div className="flex items-center justify-between">
      <h3 className="text-lg font-bold text-slate-100">
        البدائل المقترحة ({alternativesCount})
      </h3>
      <button
        onClick={onProposeAlternative}
        className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold rounded-lg transition-colors"
      >
        <Plus className="w-4 h-4" />
        <span>اقترح بديل</span>
      </button>
    </div>
  )
}

interface AlternativesListProps {
  alternatives: ForeignWord['alternatives']
  topAlternativeId?: string
  userVotes: Record<string, VoteType>
  getUserById: (userId: string) => UserType | undefined
  onVote: (alternativeId: string, voteType: VoteType) => void
}

function AlternativesList({
  alternatives,
  topAlternativeId,
  userVotes,
  getUserById,
  onVote
}: AlternativesListProps) {
  if (!alternatives || alternatives.length === 0) {
    return <EmptyAlternatives />
  }

  return (
    <div className="space-y-3">
      {alternatives.map((alternative, index) => (
        <AlternativeItem
          key={alternative.id}
          alternative={alternative}
          index={index}
          isTopVoted={topAlternativeId === alternative.id}
          proposer={getUserById(alternative.proposedBy)}
          userVote={userVotes[alternative.id]}
          onVote={onVote}
        />
      ))}
    </div>
  )
}

function EmptyAlternatives() {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center mb-3">
        <Plus className="w-6 h-6 text-slate-600" />
      </div>
      <p className="text-slate-400 mb-3">لا توجد بدائل مقترحة حالياً</p>
      <p className="text-sm text-slate-500">كن أول من يقترح بديل لهذه الكلمة</p>
    </div>
  )
}

interface AlternativeItemProps {
  alternative: ForeignWord['alternatives'][0]
  index: number
  isTopVoted: boolean
  proposer?: UserType
  userVote?: VoteType
  onVote: (alternativeId: string, voteType: VoteType) => void
}

function AlternativeItem({
  alternative,
  index,
  isTopVoted,
  proposer,
  userVote,
  onVote
}: AlternativeItemProps) {
  return (
    <div
      className={`relative bg-slate-950 border rounded-xl p-5 transition-all ${
        isTopVoted
          ? 'border-amber-500/30 bg-amber-500/5'
          : 'border-slate-800 hover:border-slate-700'
      }`}
    >
      <RankBadge index={index} />
      <AlternativeText text={alternative.text} explanation={alternative.explanation} />
      <AlternativeFooter
        alternative={alternative}
        proposer={proposer}
        userVote={userVote}
        onVote={onVote}
      />
    </div>
  )
}

interface RankBadgeProps {
  index: number
}

function RankBadge({ index }: RankBadgeProps) {
  return (
    <div className="absolute top-3 left-3">
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
          index === 0
            ? 'bg-amber-500 text-slate-900'
            : index === 1
            ? 'bg-rose-500/20 text-rose-400'
            : 'bg-slate-800 text-slate-500'
        }`}
      >
        {index + 1}
      </div>
    </div>
  )
}

interface AlternativeTextProps {
  text: string
  explanation: string
}

function AlternativeText({ text, explanation }: AlternativeTextProps) {
  return (
    <div className="text-right pr-10 mb-3">
      <div className="text-2xl font-bold text-slate-100 mb-2">
        {text}
      </div>
      <p className="text-sm text-slate-400 leading-relaxed">
        {explanation}
      </p>
    </div>
  )
}

interface AlternativeFooterProps {
  alternative: ForeignWord['alternatives'][0]
  proposer?: UserType
  userVote?: VoteType
  onVote: (alternativeId: string, voteType: VoteType) => void
}

function AlternativeFooter({ alternative, proposer, userVote, onVote }: AlternativeFooterProps) {
  return (
    <div className="flex items-center justify-between pt-3 border-t border-slate-800">
      <div className="flex items-center gap-3">
        <VoteButtons
          alternativeId={alternative.id}
          upvotes={alternative.upvotes ?? (alternative.voteCount > 0 ? alternative.voteCount : 0)}
          downvotes={alternative.downvotes ?? (alternative.voteCount < 0 ? Math.abs(alternative.voteCount) : 0)}
          userVote={userVote}
          onVote={onVote}
        />
      </div>

      {proposer && (
        <div className="text-sm text-slate-500">
          اقترحها <ClickableUser userId={proposer.id} userName={proposer.name} className="text-slate-500" />
        </div>
      )}
    </div>
  )
}
